// src/hooks/useTranslateSingle.js
// Hook khusus untuk menerjemahkan satu objek (bukan array)
// Digunakan di ViewStories, ViewProgram, dll.

import { useState, useEffect, useRef } from "react";

const MYMEMORY_URL = "https://api.mymemory.translated.net/get";
const cache = {};

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
};

async function translateText(text, target) {
  if (!text || target === "id") return text;

  const plainText = stripHtml(text);
  if (!plainText) return text;

  const cacheKey = `${target}::${plainText.substring(0, 80)}`; // key pakai 80 char pertama
  if (cache[cacheKey]) return cache[cacheKey];

  try {
    const params = new URLSearchParams({
      q:        plainText,
      langpair: `id|${target}`,
      // de: "emailkamu@gmail.com",
    });

    const res = await fetch(`${MYMEMORY_URL}?${params}`);
    if (!res.ok) throw new Error("MyMemory error");

    const data = await res.json();
    const result =
      data.responseStatus === 200
        ? data.responseData.translatedText
        : plainText;

    cache[cacheKey] = result;
    return result;
  } catch {
    return plainText;
  }
}

/**
 * Hook untuk menerjemahkan satu objek data dari API.
 *
 * @param {object}   item     - satu objek data (mis. story, program)
 * @param {string}   language - "EN" atau "ID"
 * @param {string[]} fields   - field plain text yang diterjemahkan, mis. ["judul","kategori"]
 * @param {string[]} htmlFields - field HTML (Quill) yang di-strip dulu, mis. ["deskripsi"]
 * @returns {{ translated: object, loading: boolean }}
 */
export function useTranslateSingle(item, language, fields = [], htmlFields = []) {
  const [translated, setTranslated] = useState(item);
  const [loading, setLoading]       = useState(false);
  const sessionCache = useRef({});

  useEffect(() => {
    if (!item) return;

    // Bahasa ID → kembalikan asli tanpa request
    if (language === "ID") {
      setTranslated(item);
      return;
    }

    const target   = language.toLowerCase();
    const cacheKey = `${target}::single::${item.id}`;

    if (sessionCache.current[cacheKey]) {
      setTranslated(sessionCache.current[cacheKey]);
      return;
    }

    setLoading(true);

    const run = async () => {
      // 1. Terjemahkan field plain text biasa
      const plainResults = await Promise.all(
        fields.map((f) => translateText(item[f] ?? "", target))
      );

      // 2. Terjemahkan field HTML (strip dulu, lalu terjemahkan)
      const htmlResults = await Promise.all(
        htmlFields.map((f) => translateText(item[f] ?? "", target))
      );

      const patch = {};
      fields.forEach((f, i)      => { patch[f] = plainResults[i]; });
      htmlFields.forEach((f, i)  => { patch[f] = htmlResults[i]; }); // hasil plain text

      const result = { ...item, ...patch };
      sessionCache.current[cacheKey] = result;
      setTranslated(result);
      setLoading(false);
    };

    run();
  }, [item, language]);

  return { translated, loading };
}